import React, { ReactNode } from 'react'

import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'

import { Url, Id } from '@/types'
import { CardSize } from '@/types/ui'
import { type ImageProps } from '@components/Image'

import CardContent from './CardContent'
import CardDescription from './CardDescription'
import CardFooter from './CardFooter'
import CardMetadata from './CardMetadata'
import { CardOverlay, type CardOverlayProps } from './CardOverlay'
import CardTitle from './CardTitle'
import CardWrapper from './CardWrapper'

export interface CardProps {
  id: Id
  image?: ImageProps
  title?: string
  description?: string
  metadata?: string | ReactNode
  size?: CardSize
  backgroundColor?: string
  displayFlag?: boolean
  displayFavorite?: boolean
  aspectRatio?: string
  overlay?: CardOverlayProps
  url?: Url
  children?: ReactNode
  tag?: string
}

const overlayVariants = tv({
  base: 'text-white text-[34px] leading-[38px]',
  variants: {
    position: {
      top: 'pt-[56px]',
      bottom: 'pb-6',
      left: '',
    },
    hasFlag: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    {
      hasFlag: true,
      position: 'top',
      className: 'pt-[56px]',
    },
  ],
  defaultVariants: {
    position: 'bottom',
  },
})

const descriptionVariants = tv({
  base: 'text-balance line-clamp-4',
  variants: {
    size: {
      sm: 'text-base',
      md: 'text-lg leading-[22px]',
      lg: 'text-lg leading-[24px]',
      xl: 'text-xl',
      default: 'text-lg',
    },
  },
})

// Déclarez un type pour inclure les propriétés statiques
interface CardComponent extends React.FC<CardProps> {
  Wrapper: typeof CardWrapper
  Content: typeof CardContent
  Overlay: typeof CardOverlay
  Footer: typeof CardFooter
  Metadata: typeof CardMetadata
  Title: typeof CardTitle
  Description: typeof CardDescription
}

export const Card: CardComponent = ({
  image,
  id,
  title,
  metadata,
  backgroundColor,
  size = 'lg',
  displayFlag = true,
  displayFavorite = true,
  aspectRatio,
  description,
  overlay = {
    overlayHeight: 'half',
    position: 'bottom',
    className: '',
  },
  url,
  children,
  tag,
}) => {
  const childrenArray = React.Children.toArray(children)
  const customTitle = childrenArray.find(
    (child) => (child as React.ReactElement).type === Card.Title,
  )
  const customDescription = childrenArray.find(
    (child) => (child as React.ReactElement).type === Card.Description,
  )
  const customFooter = childrenArray.find(
    (child) => (child as React.ReactElement).type === Card.Footer,
  )

  return (
    <Card.Wrapper
      className="gap-y-4 h-full w-full flex flex-col"
      data-testid={`card-${id}`}
    >
      <Card.Content
        url={url}
        image={image}
        id={id}
        title={title}
        displayFlag={displayFlag}
        displayFavorite={displayFavorite}
        backgroundColor={backgroundColor}
        aspectRatio={aspectRatio}
        className="flex-1"
        size={size}
        tag={tag}
        overlayPosition={overlay.position}
        hasDescription={description ? true : false}
      >
        {title && (
          <Card.Overlay
            overlayHeight={overlay.overlayHeight}
            position={overlay.position}
            size={size}
            className={twMerge(
              '',
              overlayVariants({
                position: overlay.position,
                hasFlag: displayFlag,
              }),
              overlay.className,
            )}
          >
            {customTitle || (
              <Card.Title className="font-[300]">{title}</Card.Title>
            )}
          </Card.Overlay>
        )}
      </Card.Content>

      {customFooter || (
        <Card.Footer className="gap-y-2 md:gap-y-3 flex-1">
          {customDescription ||
            (description && (
              <Card.Description
                className={descriptionVariants({
                  size: size,
                })}
                url={url}
              >
                {description}
              </Card.Description>
            ))}
          {metadata && (
            <Card.Metadata className="text-[#787878] text-base font-[450]">
              {metadata}
            </Card.Metadata>
          )}
        </Card.Footer>
      )}
    </Card.Wrapper>
  )
}

Card.Wrapper = CardWrapper
Card.Content = CardContent
Card.Overlay = CardOverlay
Card.Footer = CardFooter
Card.Metadata = CardMetadata
Card.Title = CardTitle
Card.Description = CardDescription

export default Card
